import { api } from '@/api/client'
import { useHelpScope } from '@/components/help/HelpScope'
import { getHelpContent } from '@/help/content'
import type { HelpPage } from '@/help/types'
import { cn } from '@/lib/cn'
import { useQuery } from '@tanstack/react-query'
import { Link, useLocation } from '@tanstack/react-router'
import { AnimatePresence, motion } from 'framer-motion'
import {
  AlertTriangle,
  ArrowUpRight,
  BookOpen,
  ChevronDown,
  ExternalLink,
  GraduationCap,
  HelpCircle,
  ListOrdered,
  MessageCircleQuestion,
  Tags,
  X,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

export function HelpFab() {
  const [open, setOpen] = useState(false)
  const location = useLocation()
  const scope = useHelpScope()
  const { i18n } = useTranslation()

  // i18n.language is included in deps so the help content rebuilds when
  // the user switches language (FR ↔ EN).
  const content = useMemo(
    () => getHelpContent(location.pathname, scope, i18n.language),
    [location.pathname, scope, i18n.language],
  )

  // Fermer le panel à chaque changement de route
  // biome-ignore lint/correctness/useExhaustiveDependencies: effect must re-run on path change
  useEffect(() => {
    setOpen(false)
  }, [location.pathname])

  // Esc pour fermer
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  return (
    <>
      <FabButton open={open} onClick={() => setOpen((o) => !o)} />
      {createPortal(
        <AnimatePresence>
          {open ? (
            <HelpPanel
              key="help-panel"
              content={content}
              pathname={location.pathname}
              onClose={() => setOpen(false)}
            />
          ) : null}
        </AnimatePresence>,
        document.body,
      )}
    </>
  )
}

// ============================================================
// FAB
// ============================================================

function FabButton({ open, onClick }: { open: boolean; onClick: () => void }) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      aria-label={open ? "Fermer l'aide" : "Ouvrir l'aide"}
      aria-expanded={open}
      initial={{ opacity: 0, scale: 0.8, y: 10 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.2, delay: 0.4 }}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      className={cn(
        'fixed right-4 bottom-4 z-40 md:right-6 md:bottom-6',
        'flex h-12 w-12 items-center justify-center rounded-full',
        'bg-primary text-primary-foreground shadow-lg ring-1 ring-primary/10',
        'transition-colors hover:bg-primary-hover',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      )}
    >
      <AnimatePresence mode="wait" initial={false}>
        {open ? (
          <motion.span
            key="close"
            initial={{ rotate: -90, opacity: 0 }}
            animate={{ rotate: 0, opacity: 1 }}
            exit={{ rotate: 90, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="flex"
          >
            <X className="h-5 w-5" />
          </motion.span>
        ) : (
          <motion.span
            key="help"
            initial={{ rotate: 90, opacity: 0 }}
            animate={{ rotate: 0, opacity: 1 }}
            exit={{ rotate: -90, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="flex"
          >
            <HelpCircle className="h-5 w-5" strokeWidth={2.25} />
          </motion.span>
        )}
      </AnimatePresence>
    </motion.button>
  )
}

// ============================================================
// PANEL
// ============================================================

interface HelpPanelProps {
  content: HelpPage | null
  pathname: string
  onClose: () => void
}

function HelpPanel({ content, pathname, onClose }: HelpPanelProps) {
  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        className="fixed inset-0 z-40 bg-background/20 backdrop-blur-[2px]"
        aria-hidden
        onClick={onClose}
      />

      <motion.aside
        initial={{ x: '100%', opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: '100%', opacity: 0 }}
        transition={{ type: 'spring', damping: 32, stiffness: 320 }}
        aria-label={`Aide — ${content?.title ?? 'Aucune aide disponible'}`}
        className={cn(
          'fixed top-0 right-0 bottom-0 z-50 flex w-full flex-col sm:w-[26rem]',
          'border-l border-border bg-background shadow-2xl',
          'dark:border-border',
        )}
      >
        <PanelHeader title={content?.title ?? 'Aide'} onClose={onClose} />

        <div className="flex-1 overflow-y-auto">
          {content ? (
            <PanelBody content={content} onNavigate={onClose} />
          ) : (
            <EmptyState pathname={pathname} />
          )}
          <LearnArticleLink pathname={pathname} onNavigate={onClose} />
        </div>

        {content?.docUrl ? <PanelFooter href={content.docUrl} /> : null}
      </motion.aside>
    </>
  )
}

function PanelHeader({ title, onClose }: { title: string; onClose: () => void }) {
  return (
    <div className="flex items-center justify-between border-b border-border px-5 py-4">
      <div className="flex items-center gap-2.5">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-muted text-foreground">
          <BookOpen className="h-3.5 w-3.5" />
        </span>
        <div className="flex flex-col leading-tight">
          <span className="text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
            Guide
          </span>
          <h2 className="text-sm font-semibold text-foreground">{title}</h2>
        </div>
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Fermer"
        className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}

function PanelBody({
  content,
  onNavigate,
}: {
  content: HelpPage
  onNavigate: () => void
}) {
  return (
    <div className="space-y-6 p-5">
      <p className="text-sm leading-relaxed text-muted-foreground">{content.summary}</p>

      {content.warnings?.length ? <Warnings items={content.warnings} /> : null}

      {content.steps?.length ? <Steps items={content.steps} /> : null}

      {content.fields?.length ? <Fields items={content.fields} /> : null}

      {content.faq?.length ? <Faq items={content.faq} /> : null}

      {content.relatedLinks?.length ? (
        <RelatedLinks items={content.relatedLinks} onNavigate={onNavigate} />
      ) : null}
    </div>
  )
}

function SectionHeading({
  icon: Icon,
  label,
}: {
  icon: typeof BookOpen
  label: string
}) {
  return (
    <div className="mb-3 flex items-center gap-2">
      <Icon className="h-3.5 w-3.5 text-muted-foreground" />
      <h3 className="text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
        {label}
      </h3>
    </div>
  )
}

function Warnings({ items }: { items: string[] }) {
  return (
    <div className="space-y-2">
      {items.map((w, i) => (
        <div
          key={i}
          className={cn(
            'flex gap-2.5 rounded-xl px-3.5 py-2.5 text-xs leading-relaxed',
            'border border-border bg-muted text-foreground',
            'dark:border-border',
          )}
        >
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
          <span>{w}</span>
        </div>
      ))}
    </div>
  )
}

function Steps({ items }: { items: NonNullable<HelpPage['steps']> }) {
  return (
    <section>
      <SectionHeading icon={ListOrdered} label="Étapes" />
      <ol className="space-y-3">
        {items.map((step, i) => (
          <li key={i} className="flex gap-3">
            <span
              className={cn(
                'flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full',
                'text-[11px] font-semibold',
                'bg-primary text-primary-foreground',
              )}
            >
              {i + 1}
            </span>
            <div className="min-w-0 flex-1 pt-0.5">
              <p className="text-sm font-medium text-foreground">{step.label}</p>
              {step.detail ? (
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{step.detail}</p>
              ) : null}
            </div>
          </li>
        ))}
      </ol>
    </section>
  )
}

function Fields({ items }: { items: NonNullable<HelpPage['fields']> }) {
  return (
    <section>
      <SectionHeading icon={Tags} label="Définitions" />
      <dl className="space-y-2">
        {items.map((f, i) => (
          <div key={i} className="rounded-xl border border-border bg-background px-3.5 py-2.5">
            <dt className="font-mono text-xs font-semibold text-foreground">{f.name}</dt>
            <dd className="mt-1 text-xs leading-relaxed text-muted-foreground">{f.definition}</dd>
          </div>
        ))}
      </dl>
    </section>
  )
}

function Faq({ items }: { items: NonNullable<HelpPage['faq']> }) {
  const [expanded, setExpanded] = useState<number | null>(null)
  return (
    <section>
      <SectionHeading icon={MessageCircleQuestion} label="Questions fréquentes" />
      <div className="space-y-1.5">
        {items.map((item, i) => {
          const isOpen = expanded === i
          return (
            <div
              key={i}
              className={cn(
                'overflow-hidden rounded-xl border transition-colors',
                isOpen ? 'border-border' : 'border-border',
              )}
            >
              <button
                type="button"
                onClick={() => setExpanded(isOpen ? null : i)}
                className={cn(
                  'flex w-full items-start justify-between gap-2 px-3.5 py-2.5 text-left text-xs',
                  'transition-colors hover:bg-muted',
                )}
                aria-expanded={isOpen}
              >
                <span className="leading-relaxed font-medium text-foreground">{item.q}</span>
                <ChevronDown
                  className={cn(
                    'mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-muted-foreground transition-transform duration-200',
                    isOpen && 'rotate-180',
                  )}
                />
              </button>
              <AnimatePresence initial={false}>
                {isOpen ? (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.18 }}
                    className="overflow-hidden border-t border-border bg-muted/60"
                  >
                    <p className="px-3.5 py-2.5 text-xs leading-relaxed text-muted-foreground">
                      {item.a}
                    </p>
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>
          )
        })}
      </div>
    </section>
  )
}

function RelatedLinks({
  items,
  onNavigate,
}: {
  items: NonNullable<HelpPage['relatedLinks']>
  onNavigate: () => void
}) {
  return (
    <section>
      <SectionHeading icon={ArrowUpRight} label="Voir aussi" />
      <div className="space-y-1">
        {items.map((link, i) => (
          <Link
            key={i}
            to={link.path}
            onClick={onNavigate}
            className={cn(
              'group flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium',
              'text-muted-foreground',
              'hover:bg-muted hover:text-foreground',
              'transition-colors',
            )}
          >
            <span className="flex-1">{link.label}</span>
            <ArrowUpRight className="h-3 w-3 text-foreground transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-muted-foreground" />
          </Link>
        ))}
      </div>
    </section>
  )
}

function LearnArticleLink({
  pathname,
  onNavigate,
}: {
  pathname: string
  onNavigate: () => void
}) {
  const { data } = useQuery({
    queryKey: ['learn', 'by-route', pathname],
    queryFn: () => api.learn.getArticleByRoute(pathname),
    staleTime: 5 * 60 * 1000,
    retry: false,
  })

  if (!data) return null

  return (
    <div className="border-t border-border px-5 py-4">
      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        Centre d'apprentissage
      </p>
      <Link
        to="/learn/articles/$slug"
        params={{ slug: data.slug }}
        onClick={onNavigate}
        className={cn(
          'group flex items-start gap-3 rounded-xl border border-border px-3 py-2.5',
          'transition-colors hover:bg-muted',
        )}
      >
        <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-muted">
          <GraduationCap className="h-3.5 w-3.5 text-foreground" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-foreground">{data.title}</span>
          {data.excerpt ? (
            <span className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
              {data.excerpt}
            </span>
          ) : null}
        </span>
        <ArrowUpRight className="mt-1 h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
      </Link>
    </div>
  )
}

function EmptyState({ pathname }: { pathname: string }) {
  return (
    <div className="flex h-full flex-col items-center justify-center px-6 py-12 text-center">
      <span className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-muted text-muted-foreground">
        <BookOpen className="h-5 w-5" />
      </span>
      <h3 className="mb-1.5 text-sm font-semibold text-foreground">Aucune aide pour cette page</h3>
      <p className="max-w-xs text-xs leading-relaxed text-muted-foreground">
        Le contenu d'aide pour <code className="font-mono text-foreground">{pathname}</code> n'est
        pas encore rédigé. Contactez votre référent IMPACT SOLUTIONS pour signaler le besoin.
      </p>
    </div>
  )
}

function PanelFooter({ href }: { href: string }) {
  return (
    <div className="border-t border-border px-5 py-3">
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          'inline-flex items-center gap-1.5',
          'text-xs font-medium text-muted-foreground hover:text-foreground',
          'transition-colors',
        )}
      >
        Documentation complète
        <ExternalLink className="h-3 w-3" />
      </a>
    </div>
  )
}
